import * as React from "react";
import { ITriggerManualJob } from "../../../redux/types/ITriggerManualJob";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import { triggerManualJob } from "../../../redux/thunks/HomeThunks";
import { ComboBox } from "@fluentui/react";
import { SaveAndClose } from "./SaveAndClose";
import { ControlledMultiselectComboBox } from "../../types/JobParameterControls/MultiselectJobParameterControl";

export interface ISuspenseReleaseParametersProps {
    createNewJob: () => any;
    triggerManualJob: typeof triggerManualJob;
    manualJobParameters: ITriggerManualJob;
    formControls: any;
    scheduledJobSet?: (value: boolean) => void;
    onError?: (errorMessage: string) => void;
}

const SuspenseReleaseParameters = (props: ISuspenseReleaseParametersProps) => {
    const [data, setData] = React.useState(() => {
        return props.formControls.map(item => {
            const newItem = { ...item };
            if (item.Type === 'combobox') {
                newItem.CurrentValue = item.CurrentValue || '';
            }
            return newItem;
        });
    });

    const [generalError, setGeneralError] = React.useState<string[]>([]);
    const [isIPLevelSelected, setIsIPLevelSelected] = React.useState<boolean>(false);

    const handleTriggerManualJob = async (params) => {
        const errors = validateFields();
        if (errors.length === 0) {
            try { 
                await props.triggerManualJob(data);
                props.scheduledJobSet(true);
                return true;
            } catch (err) {
                props.onError?.(err?.toString() || "An error occurred");
                return false;
            }
        }
        return false;
    };

    const validateFields = (): string[] => {
        let errors: string[] = [];

        data.forEach(item => {
            if (item.ControlID === 'SR_Level' || item.ControlID === 'SR_Distribution' || item.ControlID === 'SR_DistributionType') {
                if (!item.CurrentValue || item.CurrentValue === '') {
                    errors.push(`${item.Caption} is required`);
                }
            }
        });

        setGeneralError(errors);
        return errors;
    };

    const handleChange = (formItem: any, value?: any) => {
        setData(prevState => {
            const newState = prevState.map(item => {
                if (item.ControlID === 'SR_Level' && formItem.ControlID === 'SR_Level') {
                    setIsIPLevelSelected(value === 'IP');
                    return { ...item, CurrentValue: value };
                }

                if (item.ControlID === 'SR_DistributionType' && item.CurrentValue.includes('Foreign') && formItem.ControlID === 'SR_Level' && value === 'IP') {
                    return { ...item, CurrentValue: '' };
                }

                if (item.ControlID === formItem.ControlID) {
                    return { ...item, CurrentValue: value };
                }

                return item;
            });
            return newState;
        });
    };

    const getFormComponents = (jobControls) => {
        const components = jobControls.map((formItem) => {
            if(formItem.ControlID === 'SR_SourceDistribution') {
                let options: { code: string; description: string }[];
                if (typeof formItem.Options == "string")
                    options = formItem.DataSourceRepo[formItem.Options].Read();
                else options = formItem.Options;

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <ControlledMultiselectComboBox
                            caption={formItem.Caption}
                            options={options}
                            defaultValue={formItem.CurrentValue || []}
                            onSelectionChange={(keys) => { formItem.CurrentValue = keys; }}
                        />
                    </div>
                )
            }
            else if (formItem.ControlID === 'SR_Distribution') {
                let options: { key: string; text: string }[];
                if (typeof formItem.Options == "string")
                    options = formItem.DataSourceRepo[formItem.Options].Read().map((item) => ({ key: item.code, text: item.description }));
                else options = formItem.Options;

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <ComboBox
                            selectedKey={formItem.CurrentValue}
                            label={formItem.Caption}
                            options={options}
                            onChange={(e, selectedOption) => {
                                if (selectedOption) handleChange(formItem, selectedOption.key);
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            allowFreeform={true}
                        />
                    </div>
                )
            }
            else if (formItem.ControlID === 'SR_MinSuspenseAmount') {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <TextDataInput
                            label={formItem.Caption}
                            value={formItem.CurrentValue || ''}
                            changeData={(value) => handleChange(formItem, value)}
                            fieldType={"integer"}
                            fieldName={formItem.ControlID}
                            isHidden={false}
                        />
                    </div>
                )
            }
            else if (formItem.ControlID === 'SR_DistributionType') {
                let options = isIPLevelSelected ? formItem.Options.filter(opt => opt.code === 'Standard') : formItem.Options;

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <ComboBox
                            selectedKey={formItem.CurrentValue}
                            label={formItem.Caption}
                            options={options.map((option) => 
                                ({ key: option.code, text: option.description }))
                            }
                            onChange={(e, selectedOption) => {
                                if (selectedOption) handleChange(formItem, selectedOption.key);
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            allowFreeform={true}
                        />
                    </div>
                )
            }
            else if (formItem.Type === 'combobox') {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12" key={formItem.Caption}>
                        <ComboBox
                            selectedKey={formItem.CurrentValue}
                            label={formItem.Caption}
                            options={formItem.Options.map((option) => 
                                ({ key: option.code, text: option.description }))
                            }
                            onChange={(e, selectedOption) => {
                                if (selectedOption) handleChange(formItem, selectedOption.key);
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            autoComplete="on"
                            allowFreeform={true}
                        />
                    </div>
                )
            }
            
            return null;
        }).filter(Boolean);

        return components;
    };

    return (
        <div className="form-group col-md-12 col-sm-12 col-xs-12">
            <div className="row">{data && getFormComponents(data)}</div>
            {generalError.length > 0 && (
                <div className="alert alert-danger alert-dismissible">
                    <span>{generalError.join(', ')}</span>
                    <button type="button" className="close" aria-label="Close" onClick={() => setGeneralError([])}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            )}
            <div className="row">
                <SaveAndClose
                    manualJobParameters={props.manualJobParameters}
                    triggerManualJob={handleTriggerManualJob}
                    createNewJob={props.createNewJob}
                    scheduledJobSet={props.scheduledJobSet}
                    onError={props.onError}
                />
            </div>
        </div>
    );
};

export default SuspenseReleaseParameters;